import { Image, Page, Text, View } from "@react-pdf/renderer";
import WOPdfHeader from "./WOPdfHeader";
import WOPdfInfo from "./WOPdfInfo";
import {
  parsedJsonString,
  sizesWithQty,
  stripHtml,
} from "utils/UtilityFuncs";

// Floor height for a product info row, shared with the Qty rule in the size
// column so the two line up.
//
// MUST be applied as minHeight, never height: react-pdf clips overflow, so a
// fixed height that the text doesn't quite fit into renders the row BLANK --
// label and value both -- rather than overflowing visibly. That shipped once
// and made the Color row disappear in production.
const INFO_ROW_HEIGHT = 18;

export default function WOPdfProducts({
  styles,
  locationLogo,
  logo,
  selectedWO,
  approvalColorMap,
  productsList,
  showContactShipping = true,
  showStatuses = false,
  // When true the top identity band is not drawn (customer-approval send).
  hideHeader = false,
}) {
  const getApproveColor = (status) => {
    if (status === "approved") return "#198754";
    if (status === "rejected") return "#dc3545";
    return "#6c757d";
  };

  // ONE definition of "does this page get the identity band", used both to
  // draw the band and to decide whether the page needs its own W number.
  // Written twice, the two would eventually disagree and a page would end up
  // with the number printed twice or not at all.
  const headerShown = (pagesIndex) => pagesIndex === 0 && !hideHeader;
  /*   All States
   ********************************************* */

  /*   All Functions
   ********************************************* */
  const limitFirstPage = 2; // Limit for the first page
  const limitOtherPages = 3; // Limit for subsequent pages

  const getTotalPages = (list) => {
    if (!list || list.length === 0) {
      // No products to paginate -- still need page 1 if it's the only page
      // carrying the header / Contact & Shipping info, or PDF export ends
      // up with zero pages and downloads/opens as empty.
      return showContactShipping ? 1 : 0;
    }

    const firstPageItems = Math.min(list.length, limitFirstPage);
    const remainingItems = list.length - firstPageItems;

    return 1 + Math.ceil(remainingItems / limitOtherPages);
  };

  /*   All UseEffects
   ********************************************* */
  let productStyle = 0;

  return (
    <>
      {productsList &&
        Array.from({ length: getTotalPages(productsList) }, (_, pagesIndex) => {
          let sliceStart, sliceEnd;

          if (pagesIndex === 0) {
            sliceStart = 0;
            sliceEnd = limitFirstPage;
          } else {
            sliceStart = limitFirstPage + (pagesIndex - 1) * limitOtherPages;
            sliceEnd = sliceStart + limitOtherPages;
          }

          const itemsInPage = productsList.slice(sliceStart, sliceEnd);

          return (
            <Page key={pagesIndex} size="LETTER" style={styles.page}>
              {headerShown(pagesIndex) && (
                <WOPdfHeader
                  locationLogo={locationLogo}
                  logo={logo}
                  styles={styles}
                  selectedWO={selectedWO}
                />
              )}

              {/* The W number on every page that does NOT get the header band
                  -- which owns the only other copy of it. That is two cases:
                  every page after the first (the band is page-1 only), and
                  page 1 itself on the customer-approval send, which passes
                  hideHeader. Without this a continuation page, or an entire
                  approval PDF, carries nothing saying which order it is.

                  Deliberately NOT on each product card: the number would then
                  repeat on every block down the page. One per page, top right,
                  where the band puts it, so both layouts read the same. */}
              {!headerShown(pagesIndex) && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    // NO bottom margin, and 9pt is a CEILING, not a taste
                    // call. A continuation page carries three product cards
                    // and has under 12pt of slack left; this line has to fit
                    // inside it or a card is pushed to a page of its own.
                    // Measured, not guessed: at 9pt with no margin, 5 / 8 / 11
                    // products still paginate to 2 / 3 / 4 pages exactly as
                    // they did before this line existed. 10pt, or 9pt with a
                    // 2pt margin, spills a card and turns 3 pages into 5.
                    // __tests__/WOPdfWorkOrderNumber.test.jsx asserts those
                    // page counts, so raising this fails a test rather than
                    // quietly doubling everyone's print job.
                  }}
                >
                  <Text style={{ fontSize: 9, fontWeight: "extrabold" }}>
                    W-{selectedWO?.woId}
                  </Text>
                  {/* Beside the number, not under it. On the same line this
                      costs NO extra height, and there is none to spend -- see
                      the note above. */}
                  <Text
                    style={{ fontSize: 9, marginLeft: 8, color: "#555" }}
                    render={({ pageNumber, totalPages }) =>
                      `Page ${pageNumber} of ${totalPages}`
                    }
                  />
                </View>
              )}

              {/*   Contact and Shipping Info
               ********************************************* */}
              {pagesIndex === 0 && (
                <WOPdfInfo
                  styles={styles}
                  selectedWO={selectedWO}
                  productsList={productsList}
                  showContactShipping={showContactShipping}
                />
              )}
              {/*   ALL Product Details
               ********************************************* */}
              <View style={[styles.productDetails, { marginTop: 10 }]}>
                {/* ----------  Looping Products   ---------- */}
                {itemsInPage.map((item, index) => {
                  productStyle += 1;
                  return (
                    <View style={styles.mainContainer} key={item?.id}>
                      {/* Header row is three cells: the product NUMBER in a
                          narrow box, then product type, then
                          "<style number> : <description>". The number leads so
                          the eye can run straight down the numbers -- it is
                          what gets quoted back ("item 3 is wrong") -- and the
                          style then reads as one phrase. Matches the customer
                          approval card. Widths must total 100%: 7 + 25 + 68. */}
                      <View style={styles.prodMain}>
                        <Text
                          style={{
                            ...styles.prodTitle,
                            width: "7%",
                            fontSize: "12px",
                            borderRight: "1px solid #000",
                            fontWeight: "bold",
                            textAlign: "center",
                            // prodTitle's 10px side padding would leave ~18pt
                            // of text width in a 7% cell -- fine for "9", tight
                            // for a three-digit product number. react-pdf clips
                            // rather than overflowing, so give the digits room.
                            padding: "5px 2px",
                            // The same green as the number box on the customer
                            // approval card, so the printed sheet and the card
                            // the customer approved read as the same document.
                            // Literal because @react-pdf/renderer cannot read
                            // CSS custom properties -- the source of truth is
                            // --ct-success, used via .product-number-label in
                            // CustomerWoApproval.css. Change one, change both.
                            backgroundColor: "#0acf97",
                            // Near-black, matching the card. White on this
                            // green measures 2.02:1 and fails every WCAG
                            // threshold; near-black is 7.63:1.
                            color: "#212529",
                          }}
                        >
                          {productStyle}
                        </Text>
                        <Text
                          style={{
                            ...styles.prodTitle,
                            width: "25%",
                            fontSize: "12px",
                            borderRight: "1px solid #000",
                            backgroundColor: "yellow",
                            fontWeight: "bold",
                          }}
                        >
                          {item?.productTypes?.productTypes}
                        </Text>
                        <View
                          style={{
                            ...styles.prodTitle,
                            width: "68%",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text style={{ fontSize: "12px" }}>
                            {item?.productStyle} : {item?.productDescription}
                          </Text>
                          {showStatuses && (
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              {/* A - Approval Status */}
                              <View
                                style={{
                                  backgroundColor: getApproveColor(
                                    item?.approveStatus,
                                  ),
                                  borderRadius: 3,
                                  width: 16,
                                  height: 16,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginLeft: 4,
                                }}
                              >
                                <Text
                                  style={{
                                    color: "white",
                                    fontSize: 8,
                                    fontWeight: "bold",
                                  }}
                                >
                                  A
                                </Text>
                              </View>
                              {/* P - Purchased */}
                              <View
                                style={{
                                  backgroundColor: item?.purchased
                                    ? "#198754"
                                    : "#6c757d",
                                  borderRadius: 3,
                                  width: 16,
                                  height: 16,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginLeft: 4,
                                }}
                              >
                                <Text
                                  style={{
                                    color: "white",
                                    fontSize: 8,
                                    fontWeight: "bold",
                                  }}
                                >
                                  P
                                </Text>
                              </View>
                              {/* R - Received */}
                              <View
                                style={{
                                  backgroundColor: item?.received
                                    ? "#198754"
                                    : "#6c757d",
                                  borderRadius: 3,
                                  width: 16,
                                  height: 16,
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginLeft: 4,
                                }}
                              >
                                <Text
                                  style={{
                                    color: "white",
                                    fontSize: 8,
                                    fontWeight: "bold",
                                  }}
                                >
                                  R
                                </Text>
                              </View>
                              {/* Ready */}
                              <Text
                                style={{
                                  fontSize: 8,
                                  marginLeft: 5,
                                  color: item?.isReady ? "#198754" : "#6c757d",
                                  fontWeight: "bold",
                                }}
                              >
                                Ready: {item?.isReady ? "Yes" : "No"}
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>
                      <View
                        style={[
                          styles.prodMain,
                          {
                            height: "195px",
                          },
                        ]}
                      >
                        <View style={styles.prodContent}>
                          {/* Color gets a full-width row of its own: with
                              approvalColors mapped to names and joined, this
                              value is routinely the longest on the card and
                              wrapped badly in a half-width cell. Fabric drops
                              to the row below to share with Team Name. */}
                          <View
                            style={[
                              styles.prodInfoRow,
                              { minHeight: INFO_ROW_HEIGHT },
                            ]}
                          >
                            {/* Full width. prodCol is 50% and prodKey is 40%
                                OF that, so the key is overridden to 20% of the
                                whole row -- otherwise "Color:" would render
                                twice as wide as "Fabric:" below it and the two
                                label columns would no longer line up. */}
                            <View style={[styles.prodCol, { width: "100%" }]}>
                              <Text style={[styles.prodKey, { width: "20%" }]}>
                                Color:
                              </Text>
                              <Text style={[styles.prodVal, { width: "80%" }]}>
                                {approvalColorMap &&
                                item?.approvalColors?.length
                                  ? item?.approvalColors
                                      .map(
                                        (colorId) => approvalColorMap[colorId],
                                      )
                                      .join(", ")
                                  : item?.productColor}
                                {/* {approvalColorMap &&
                                parsedJsonString(item?.approvalColors).length
                                  ? parsedJsonString(item?.approvalColors)
                                      .map(
                                        (colorId) => approvalColorMap[colorId]
                                      )
                                      .join(", ")
                                  : item?.productColor} */}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.prodInfoRow}>
                            <View style={[styles.prodCol, styles.borderRight]}>
                              <Text style={styles.prodKey}>Fabric:</Text>
                              <Text style={styles.prodVal}>
                                {item?.productFabric}
                              </Text>
                            </View>
                            <View style={styles.prodCol}>
                              <Text style={styles.prodKey}>Team Name</Text>
                              <Text style={styles.prodVal}>
                                {item?.teamName}
                              </Text>
                            </View>
                          </View>
                          <View
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {item?.productImage && (
                              <Image
                                style={{
                                  width: "100%",
                                  height: 200,
                                  objectFit: "contain",
                                }}
                                src={item?.productImage}
                              />
                            )}
                          </View>
                        </View>

                        <View style={[styles.prodSizes, { paddingTop: 0 }]}>
                          <View
                            style={{
                              minHeight: INFO_ROW_HEIGHT,
                              justifyContent: "center",
                              borderBottom: "1px solid gray",
                              marginBottom: 5,
                            }}
                          >
                            <Text style={styles.prodSubHeading}>
                              Qty : {item?.totalQuantity}
                            </Text>
                          </View>

                          <Text style={styles.prodSubHeading}>Size Detail</Text>
                          {item?.productSizes &&
                            sizesWithQty(item?.productSizes).map((d) => (
                              <Text key={d.id} style={styles.prodTxt}>
                                {d?.size} = {d?.quantity}
                              </Text>
                            ))}
                        </View>
                      </View>
                      <Text style={styles.prodComments}>
                        COMMENTS: {stripHtml(item?.notes)}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </Page>
          );
        })}
    </>
  );
}
